import { FC, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { isFallbackAsset } from '@service/bugo/utils/courtesyContentGenerator';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import { Icon } from '@shared/components/icons';
import { notificateSuccess } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import html2canvas from 'html2canvas';
import { useAsync } from 'react-use';

import { courtesyAssetAtom, courtesyContentStringAtom } from './courtesy.atom';

interface Props {
  openId: string;
}

const saveImageToLocal = async (imageUrl: string) => {
  const response = await fetch(imageUrl);
  const blob = await response.blob();

  const file = new File([blob], 'image.jpeg', { type: 'image/jpeg' });
  const dataURL = URL.createObjectURL(file);

  return dataURL;
};

export const MemberCourtesyPreviewModal: FC<Props> = function MemberCourtesyPreviewModal({
  openId,
}: Props) {
  const { Download, OtherApps } = Icon();
  const courtesyAsset = useRecoilValue(courtesyAssetAtom);
  const imageUrl = courtesyAsset?.backgroundImage.url;
  const courtesyContentString = useRecoilValue(courtesyContentStringAtom);
  const divRef = useRef(null);
  const [localImageUrl, setLocalImageUrl] = useState<string>();
  const notificationInstance = useRecoilValue(notificationInstanceAtom);

  const isFallback = courtesyAsset ? isFallbackAsset(courtesyAsset._id) : false;

  useAsync(async () => {
    if (!imageUrl) return;

    if (isFallback) {
      setLocalImageUrl(imageUrl);
      return;
    }

    const corsImageModified = new Image();
    corsImageModified.crossOrigin = 'Anonymous';
    const s3Url = fileBucketUrl(`${imageUrl}`, 'raw');

    corsImageModified.src = s3Url + '?not-from-cache-please';

    const localUrl = await saveImageToLocal(corsImageModified.src);

    setLocalImageUrl(localUrl);
  }, [imageUrl, isFallback]);

  function handleDownloadClick() {
    const div_ = divRef.current;
    if (div_)
      html2canvas(div_, { useCORS: true, allowTaint: true }).then((canvas) => {
        const dataUrl = canvas.toDataURL('image/jpeg');
        const link = document.createElement('a');
        link.download = '답례인사.jpg';
        link.href = dataUrl;
        link.click();
      });
  }

  async function handleShareClick() {
    const div_ = divRef.current;
    if (div_) {
      const canvas = await html2canvas(div_, { useCORS: true, allowTaint: true });
      canvas.toBlob((blob) => {
        if (blob) {
          if (navigator.share) {
            navigator
              .share({
                files: [new File([blob], '답례인사.jpg', { type: 'image/jpeg' })],
                title: '답례인사',
                text: '답례인사를 공유합니다.',
              })
              .then(() => notificateSuccess(notificationInstance, '공유 성공!'))
              .catch(() => alert('공유에 실패했습니다.'));
          } else {
            alert(
              '이 브라우저는 공유기능을 지원하지 않습니다. 다운로드 받으셔서 직접 공유해주세요',
            );
          }
        }
      }, 'image/jpeg');
    }
  }

  return (
    <Modal openId={openId}>
      <div className="relative" ref={divRef}>
        <img src={localImageUrl} />
        <div
          className={`noto-serif-kr absolute z-10 w-full whitespace-pre-wrap break-keep font-medium`}
          style={{
            fontSize: `${courtesyAsset?.textBox?.y1 ?? 12}px`,
            top: `${courtesyAsset?.textBox?.y0 ?? 30}px`,
            paddingLeft: `${courtesyAsset?.textBox?.x0 ?? 24}px`,
            paddingRight: `${courtesyAsset?.textBox?.x1 ?? 24}px`,
          }}
        >
          <p>{courtesyContentString}</p>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          onClick={handleShareClick}
          className="button center-box gap-2 font-medium text-black"
        >
          <OtherApps className="wh-6" />
          <p>공유</p>
        </Button>
        <Button
          onClick={handleDownloadClick}
          className="button center-box gap-2 font-medium text-black"
        >
          <Download className="wh-6 fill-gray-700" />
          <p>다운로드</p>
        </Button>
      </div>
    </Modal>
  );
};
